import React from 'react'
import { useState, useEffect } from 'react'
import type { Translation } from '../../i18n'
import type { Dest } from '../../types/dest'
import { listProducts, type ApiProduct, type ApiLang } from '../../api/caApi'
import { useCart } from '../../state/CartContext'
import AddToCartButton from '../../components/AddToCartButton'
import DestinationSelector from '../../components/DestinationSelector'
import { serif, warm900, warm700, warm500, stone, textMuted } from './tokens'

type Props = {
  locale: string
  l: Translation['landing']
  t: Translation
}

const LandingProductCard: React.FC<{
  product: ApiProduct
  highlight: boolean
  t: Translation
}> = ({ product, highlight, t }) => {
  const { add } = useCart()
  const l = t.landing
  const firstPhoto = [...product.photos].sort((a, b) => a.sort - b.sort)[0]
  const imgUrl = firstPhoto?.url || '/apps/ca/img/catalog/product_bread_03.jpg'

  return (
    <div
      className="bg-white rounded-[2px] overflow-hidden transition-transform hover:-translate-y-1"
      style={highlight ? { border: '2px solid #c4a882' } : {}}
    >
      {highlight && (
        <div
          className="text-[0.68rem] font-medium tracking-[0.1em] uppercase py-1.5 text-center"
          style={{ background: '#c4a882', color: warm900 }}
        >
          {l.products.highlight}
        </div>
      )}
      <div className="h-[220px] overflow-hidden">
        <img
          src={imgUrl}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.04]"
        />
      </div>
      <div className="p-6">
        <h3 className="font-semibold text-[1.3rem] mb-1" style={{ fontFamily: serif, color: warm900 }}>
          {product.name}
        </h3>
        {product.description && (
          <p className="text-[0.82rem] leading-[1.6] mb-4 line-clamp-2" style={{ color: textMuted }}>{product.description}</p>
        )}
        <div className="flex items-center justify-between pt-4" style={{ borderTop: `1px solid ${stone}` }}>
          <p className="font-semibold text-[1.5rem]" style={{ fontFamily: serif, color: warm700 }}>
            € {product.price.toFixed(2)}
          </p>
          <AddToCartButton
            className="text-[0.78rem] font-medium tracking-[0.08em] uppercase px-4 py-2 rounded-[2px] transition-colors hover:opacity-90 cursor-pointer bg-[#6b4f2e] text-[#faf6ef]"
            onAdd={() => add({ id: product.slug, name: product.name, price: product.price, image: imgUrl })}
          >
            {t.product.addToCart}
          </AddToCartButton>
        </div>
      </div>
    </div>
  )
}

export const ProductsSection: React.FC<Props> = ({ locale, l, t }) => {
  const [dest, setDest] = useState<Dest>('PT')
  const [products, setProducts] = useState<ApiProduct[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    listProducts({ lang: locale as ApiLang, destination: dest })
      .then(data => { if (alive) setProducts(data) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [locale, dest])

  return (
    <section id="produtos" className="px-10 md:px-20 py-24" style={{ background: '#fdf8f2' }}>
      <div className="text-center mb-12">
        <p className="flex items-center justify-center gap-2 text-[0.75rem] font-medium tracking-[0.18em] uppercase mb-4" style={{ color: warm500 }}>
          {l.products.sectionLabel}
        </p>
        <h2
          className="text-[clamp(2rem,3.5vw,3rem)] font-light leading-[1.15] mb-2"
          style={{ fontFamily: serif, color: warm900 }}
        >
          {l.products.title}
        </h2>
        <p className="text-[0.9rem]" style={{ color: textMuted }}>{l.products.subtitle}</p>
      </div>
      <DestinationSelector
        dest={dest}
        onChange={setDest}
        banner={l.destSelector[dest].banner}
        labels={l.destSelector}
        bannerClassName="mb-10"
      />
      <div className="grid md:grid-cols-3 gap-6 max-w-[1000px] mx-auto">
        {loading && (
          <div className="md:col-span-3 text-center py-8" style={{ color: textMuted }}>{t.catalog.loading}</div>
        )}
        {products.map((p, i) => (
          <LandingProductCard key={p.slug} product={p} highlight={i === 1} t={t} />
        ))}
      </div>
    </section>
  )
}
